import * as React from "react";
import { useTranslation } from "react-i18next";
import type { ScheduleItem } from "src/types";

import AddToCalendar from "@/components/add-to-calendar/add-to-calendar-component";
import { RenderHTMLContent } from "@/components/editor/render-html-content/render-html-content-component";
import { AvatarGroup } from "@/components/editor/shared/schedules/avatar-group";
import { mapFirstLastNames } from "@/components/editor/shared/schedules/utils";
import { Typography } from "@/components/editor/shared/typography";
import { Calendar } from "@/components/icons/calendar";
import { useScheduleTimezone } from "@/hooks/use-schedule-timezone";
import getFormattedScheduleTime from "@/utils/dates/getFormattedScheduleTime";

import {
  ScheduleDescriptionWrapper,
  ScheduleDisplayItemContent,
  ScheduleDisplayItemDate,
  ScheduleDisplayItemWrapper,
  ScheduleListItem,
  StyledButton,
  StyledScheduleTime,
  StyledSegmentText,
} from "./default-schedule-component-styles";

type DefaultScheduleDisplayProps = {
  schedules: ScheduleItem[];
};

export default function DefaultScheduleDisplay(
  props: DefaultScheduleDisplayProps
) {
  return (
    <ul>
      {props.schedules?.map((schedule) => (
        <ScheduleListItem key={schedule.id} id={schedule.id.toString()}>
          <ScheduleDisplayItem schedule={schedule} />
        </ScheduleListItem>
      ))}
    </ul>
  );
}

type ScheduleDisplayItemProps = {
  schedule: ScheduleItem;
};

function ScheduleDisplayItem({ schedule }: ScheduleDisplayItemProps) {
  const { t } = useTranslation();
  const selectedTimezone = useScheduleTimezone();
  const { time, type } = React.useMemo(() => {
    const type = schedule.event_part.event_part_type;
    const customText = schedule.event_part.event_type_custom_text;

    return {
      time: getFormattedScheduleTime(
        schedule.time_start,
        schedule.time_end,
        selectedTimezone
      ),
      type: customText ?? type.charAt(0).toUpperCase() + type.slice(1),
    };
  }, [
    schedule.event_part,
    schedule.time_start,
    schedule.time_end,
    selectedTimezone,
  ]);

  const users = React.useMemo(() => {
    if (!schedule?.speakers) {
      return [];
    }
    return (
      mapFirstLastNames(schedule?.speakers).map((speaker) => ({
        firstName: speaker.first_name,
        lastName: speaker.last_name,
        image: speaker.image_url,
      })) || []
    );
  }, [schedule?.speakers]);

  return (
    <ScheduleDisplayItemWrapper>
      <ScheduleDisplayItemContent>
        <Typography pattern="headingFour" data-testid="schedule-item-name">
          {schedule.name}
        </Typography>
        <ScheduleDescriptionWrapper data-testid="schedule-item-description">
          <Typography
            as={RenderHTMLContent}
            pattern="description"
            html={schedule.description}
          />
        </ScheduleDescriptionWrapper>
        {users.length > 0 && (
          <AvatarGroup
            popupLabel="Additional speakers"
            data-testid="speaker-avatars-group"
            users={users}
          />
        )}
      </ScheduleDisplayItemContent>
      <ScheduleDisplayItemDate>
        <StyledSegmentText data-testid="schedule-item-type">
          {type}
        </StyledSegmentText>
        <StyledScheduleTime data-testid="schedule-item-time">
          {time}
        </StyledScheduleTime>
        <AddToCalendar
          className="mt-6"
          event={{
            external_id: schedule.external_id,
            time_start: schedule.time_start,
            time_end: schedule.time_end,
            name: schedule.name,
          }}
        >
          <StyledButton
            data-testid="event-to-calendar-button"
            isInline
            variant="outlined"
            pattern="secondary"
            size="small"
            title={t("preference-panel.schedule.add-to-calendar")}
          >
            <Calendar className="mr-4 size-5" />
            {t("preference-panel.schedule.add-to-calendar")}
          </StyledButton>
        </AddToCalendar>
      </ScheduleDisplayItemDate>
    </ScheduleDisplayItemWrapper>
  );
}
